import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Question, TestResults, questionsApi } from '../services/api/questions';
import { useAuthStore } from './authStore';
import { migrateHistoryEntries } from '../utils/historyMigration';
import { createSafeStorage } from '../utils/storage';
import { analytics } from '../utils/analytics';

interface HistoryEntry {
  id: string;
  date: string;
  results: TestResults;
}

interface TestState {
  test_id: string | null;
  temp_user_id: string | null;
  questions: Question[];
  currentAnswers: { [key: string]: number };
  currentResults: TestResults | null;
  isLoading: boolean;
  error: string | null;
  isComplete: boolean;
  history: HistoryEntry[];
  isOffline: boolean;
  offlineAnswers: { [key: string]: number };
  fetchQuestions: () => Promise<void>;
  setAnswer: (questionId: string, value: number) => Promise<void>;
  submitTest: () => Promise<TestResults>;
  getProgress: () => number;
  reset: () => void;
  resetTest: () => void;
  setError: (error: string | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setOffline: (isOffline: boolean) => void;
  syncOfflineData: () => Promise<void>;
  saveOfflineAnswer: (questionId: string, value: number) => void;
}

export const useTestStore = create<TestState>()(
  persist(
    (set, get) => ({
      test_id: null,
      temp_user_id: null,
      questions: [],
      currentAnswers: {},
      currentResults: null,
      isLoading: false,
      error: null,
      isComplete: false,
      history: [],
      isOffline: false,
      offlineAnswers: {},

      setError: (error: string | null) => set({ error }),
      setIsLoading: (isLoading: boolean) => set({ isLoading }),
      setOffline: (isOffline: boolean) => set({ isOffline }),

      saveOfflineAnswer: (questionId: string, value: number) => {
        set(state => ({
          offlineAnswers: {
            ...state.offlineAnswers,
            [questionId]: value,
          },
          currentAnswers: {
            ...state.currentAnswers,
            [questionId]: value,
          },
        }));

        try {
          const state = get();
          localStorage.setItem('enneagram_offline_answers', JSON.stringify({
            answers: state.offlineAnswers,
            test_id: state.test_id,
            temp_user_id: state.temp_user_id,
            lastUpdated: new Date().toISOString(),
          }));
        } catch (e) {
          console.warn('Failed to save offline answers:', e);
        }
      },

      syncOfflineData: async () => {
        const state = get();
        if (!state.isOffline || Object.keys(state.offlineAnswers).length === 0) {
          return;
        }

        try {
          set({ isLoading: true });

          for (const [questionId, value] of Object.entries(state.offlineAnswers)) {
            if (state.test_id) {
              await questionsApi.saveAnswer(state.test_id, {
                question_id: questionId,
                value,
                temp_user_id: state.temp_user_id,
              });
            }
          }

          set({ offlineAnswers: {}, isLoading: false });
          localStorage.removeItem('enneagram_offline_answers');
        } catch (error) {
          console.error('Failed to sync offline data:', error);
          set({ isLoading: false });
          throw error;
        }
      },

      reset: () => {
        set(state => {
          const newHistoryEntry = state.currentResults
            ? {
                id: crypto.randomUUID(),
                date: new Date().toISOString(),
                results: state.currentResults,
              }
            : null;

          return {
            test_id: null,
            temp_user_id: null,
            questions: [],
            currentAnswers: {},
            isLoading: false,
            error: null,
            isComplete: false,
            history: newHistoryEntry
              ? [...(state.history || []), newHistoryEntry]
              : (state.history || []),
          };
        });
      },

      resetTest: () => {
        set(state => {
          const newHistoryEntry = state.currentResults
            ? {
                id: crypto.randomUUID(),
                date: new Date().toISOString(),
                results: state.currentResults,
              }
            : null;

          return {
            test_id: null,
            temp_user_id: null,
            questions: [],
            currentAnswers: {},
            isLoading: false,
            error: null,
            isComplete: false,
            history: newHistoryEntry
              ? [...(state.history || []), newHistoryEntry]
              : (state.history || []),
          };
        });
      },

      fetchQuestions: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await questionsApi.getTestQuestions();
          set({
            test_id: response.test_id,
            temp_user_id: response.temp_user_id,
            questions: response.questions,
            currentAnswers: {},
            isLoading: false,
          });
          analytics.testStarted();
        } catch (error: any) {
          console.error('Error fetching questions:', error);
          set({
            error: error.message || 'Error fetching questions',
            isLoading: false,
          });
        }
      },

      setAnswer: async (questionId: string, value: number) => {
        const state = get();
        if (!state.test_id) {
          await state.fetchQuestions();
          return;
        }

        set(state => {
          const newAnswers = { ...state.currentAnswers, [questionId]: value };

          try {
            const progress = Math.floor((Object.keys(newAnswers).length / state.questions.length) * 100);
            localStorage.setItem('enneagram_test_progress', JSON.stringify({
              progress,
              answeredCount: Object.keys(newAnswers).length,
              totalQuestions: state.questions.length,
              test_id: state.test_id,
              lastUpdated: new Date().toISOString(),
            }));
          } catch (e) {
            console.warn('Failed to save progress to localStorage:', e);
          }

          return { currentAnswers: newAnswers };
        });

        if (!navigator.onLine) {
          state.saveOfflineAnswer(questionId, value);
          return;
        }

        try {
          await questionsApi.saveAnswer(state.test_id, {
            question_id: questionId,
            value,
            temp_user_id: state.temp_user_id,
          });
        } catch (error: any) {
          console.error('Error saving answer to server:', error);
          if (error.message?.includes('fetch') || error.message?.includes('network')) {
            state.saveOfflineAnswer(questionId, value);
          }
          // Non-network errors: answer is already saved locally, no action needed
        }
      },

      submitTest: async () => {
        const state = get();
        if (!state.test_id) {
          throw new Error('No test session found');
        }

        const progress = get().getProgress();
        if (progress < 100) {
          throw new Error('Please answer all questions before submitting');
        }

        set({ isLoading: true, error: null });
        try {
          const results = await questionsApi.submitTest(state.test_id, {
            temp_user_id: state.temp_user_id,
            answers: state.currentAnswers,
          });

          if (!results || !results.primaryType || !results.scores) {
            throw new Error('Invalid results format received from server');
          }

          const newHistoryEntry = {
            id: crypto.randomUUID(),
            date: new Date().toISOString(),
            results,
          };

          const migratedHistory = migrateHistoryEntries(get().history);

          set({
            currentResults: results,
            isComplete: true,
            isLoading: false,
            error: null,
            history: [...migratedHistory, newHistoryEntry],
          });
          analytics.testCompleted(results.primaryType, results.wing);

          try {
            const authStore = useAuthStore.getState();
            if (authStore.user && authStore.updateUser) {
              authStore.updateUser({
                hasCompletedTest: true,
                enneagramType: results.primaryType,
              });
            }
          } catch (updateError) {
            console.error('Failed to update user profile after test completion:', updateError);
          }

          return results;
        } catch (error: any) {
          console.error('Error submitting test:', error);
          set({
            error: error.message || 'Error submitting test',
            isLoading: false,
          });
          throw error;
        }
      },

      getProgress: () => {
        const state = get();
        if (!state.questions.length) return 0;
        return Math.floor((Object.keys(state.currentAnswers).length / state.questions.length) * 100);
      },
    }),
    {
      name: 'enneagram-test-storage',
      partialize: (state) => ({
        history: state.history,
        currentResults: state.currentResults,
        offlineAnswers: state.offlineAnswers,
        test_id: state.test_id,
        temp_user_id: state.temp_user_id,
        currentAnswers: state.currentAnswers,
      }),
      storage: createSafeStorage(),
    }
  )
);
